import { isEqual } from "lodash-es";
import { ThunkDispatch } from "redux-thunk";

import { isValidQuery } from "@utils/hooks/useSubmitAutocompleteSearchBox";
import {
    SearchComponentEnum,
    SearchComponentOfType,
} from "@utils/search/@types/searchComponent";
import {
    SearchComponentConfiguration,
    SearchComponentConfigurationMap,
} from "@utils/search/@types/searchConfiguration";
import { NumberOfHitsState } from "@utils/search/@types/searchState";
import { isType } from "@utils/search/type.utils";

import { AlgoliaTrackingService } from "@services/algolia/tracking/algoliaTracking.service";
import { bffSearch } from "@services/bffSearch/bffSearch.service";

import { invalidateConfiguration } from "@redux/modules/value/search/helpers/invalidation.helpers";
import {
    mergeResultsInConfiguration,
    mergeSearchResultsInState,
} from "@redux/modules/value/search/helpers/merge.helpers";
import { applyRequestFilters } from "@redux/modules/value/search/helpers/requestFilters.helpers";
import { getComponentConfigurationAndOthers } from "@redux/modules/value/search/helpers/selector.helpers";
import { set } from "@redux/modules/value/search/search.module";
import { getSearch } from "@redux/modules/value/search/search.selectors";
import {
    SearchConfiguration,
    SearchInvalidations,
    SearchResults,
    SearchState,
    SearchStatus,
} from "@redux/modules/value/search/search.types";
import { IState, TExtraThunkArgs, TValueAction } from "@redux/store.types";

const isValidSearchConfiguration = (configuration: SearchConfiguration) =>
    isValidQuery(configuration.query) || configuration.filters;

const createNoResultsSearchState = (configuration: SearchConfiguration) => ({
    status: SearchStatus.Idle,
    configuration: configuration,
    results: configuration.components
        .filter(component => isType(component, "NUMBER_OF_HITS"))
        .map(component => {
            const numberOfHitsState: NumberOfHitsState = {
                component:
                    component.component as SearchComponentOfType<"NUMBER_OF_HITS">,
                numberOfHits: 0,
            };

            return numberOfHitsState;
        }),
});

const searchByConfiguration = async (
    searchApiKey: string,
    configuration: SearchConfiguration,
    resultList: SearchResults,
    dispatch: ThunkDispatch<IState, TExtraThunkArgs, TValueAction>,
    invalidations: SearchInvalidations,
) => {
    const configurationWithInvalidation = invalidateConfiguration(
        configuration,
        invalidations.configuration,
    );

    const searchConfiguration = applyRequestFilters(
        configurationWithInvalidation,
        resultList,
    );

    if (!isValidSearchConfiguration(searchConfiguration)) {
        return dispatch(set(createNoResultsSearchState(searchConfiguration)));
    }

    dispatch(set({ status: SearchStatus.Loading }));

    try {
        const searchResponse = await bffSearch(searchApiKey)({
            ...searchConfiguration,
            userToken:
                searchConfiguration.userToken ??
                AlgoliaTrackingService.getAuthenticatedUserToken() ??
                AlgoliaTrackingService.getUserToken(),
        });

        if (searchResponse.type === "redirect") {
            window.location.assign(searchResponse.url);
        } else {
            const mergedComponentsConfiguration = mergeResultsInConfiguration(
                searchConfiguration.components,
                configurationWithInvalidation.components,
                searchResponse.results,
            );

            const mergedResults = mergeSearchResultsInState(
                resultList,
                searchResponse.results,
                invalidations.state,
            );

            dispatch(
                set({
                    status: SearchStatus.Idle,
                    configuration: {
                        ...searchConfiguration,
                        components: mergedComponentsConfiguration,
                    },
                    results: mergedResults,
                }),
            );
        }
    } catch (e) {
        dispatch(set({ status: SearchStatus.Error }));
    }
};

const searchWithNewConfiguration =
    (
        configuration: Partial<SearchConfiguration>,
        invalidations: SearchInvalidations,
        createNewConfiguration: (
            configuration: Partial<SearchConfiguration>,
            searchState: SearchState,
        ) => SearchConfiguration | undefined,
    ) =>
    async (
        dispatch: ThunkDispatch<IState, TExtraThunkArgs, TValueAction>,
        getState: () => IState,
    ) => {
        const searchState = getSearch(getState());
        if (!searchState) return;

        const newConfiguration = createNewConfiguration(
            configuration,
            searchState,
        );
        const searchApiKey = searchState.algoliaParameters?.searchApiKey;

        if (!newConfiguration || !searchApiKey) return;

        await searchByConfiguration(
            searchApiKey,
            newConfiguration,
            searchState.results,
            dispatch,
            invalidations,
        );
    };

const searchWithNewComponentConfiguration = <
    TComponent extends SearchComponentEnum,
>(
    component: TComponent,
    invalidations: SearchInvalidations,
    createNewComponentConfiguration: (
        componentConfiguration: SearchComponentConfigurationMap[TComponent],
        otherComponentConfigurations: ReadonlyArray<SearchComponentConfiguration>,
        searchState: SearchState,
    ) => SearchComponentConfiguration | undefined,
) =>
    searchWithNewConfiguration(
        {},
        invalidations,
        (_configuration, searchState) => {
            const { componentConfiguration, otherComponentConfigurations } =
                getComponentConfigurationAndOthers(searchState, component);

            if (!componentConfiguration) return;

            const newComponentConfiguration = createNewComponentConfiguration(
                componentConfiguration,
                otherComponentConfigurations,
                searchState,
            );
            if (!newComponentConfiguration) return;

            if (isEqual(componentConfiguration, newComponentConfiguration)) {
                return;
            }

            return {
                ...searchState.configuration,
                components: [
                    ...otherComponentConfigurations,
                    newComponentConfiguration,
                ],
            };
        },
    );

export {
    searchWithNewConfiguration,
    searchWithNewComponentConfiguration,
    searchByConfiguration,
};
